import { Box, IconButton, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { convertBytesToMbsOrKbs } from '../../../../attachments/attachmentsApi';
import { fetchAttachmentUrl } from '../../../../components/attachments';
import Loading from '../../../../components/Loading';
import FormModalInfoAlert from '../../../../form/FormModalInfoAlert';
import Dropzone from '../../../../form/widgets/Dropzone';
import I18n, { useI18n } from '../../../../i18n/I18n';
import { Icons } from '../../../../icons/Icons';
import { H, V } from '../../../../Layout';
import { KEY_CREDITS_AI_ACCEPTED_MIME, KEY_CREDITS_AI_MAX_UPLOAD_BYTES } from '../../../constants';
import type { UploadedCreditSourceFile } from '../../../types';

type KeyCreditsSourceStepProps = {
    text: string;
    onTextChange: (value: string) => void;
    uploadedFile: UploadedCreditSourceFile | null;
    onUploadedFileChange: (file: UploadedCreditSourceFile | null) => void;
    activeOrganizationAccount: string;
};

function getFileTypeLabel(fileName: string): string {
    const parts = fileName.split('.');
    if (parts.length < 2) {
        return '';
    }
    return parts.pop()!.toUpperCase();
}

export default function KeyCreditsSourceStep({
    text,
    onTextChange,
    uploadedFile,
    onUploadedFileChange,
    activeOrganizationAccount
}: KeyCreditsSourceStepProps) {
    const pastePlaceholder = useI18n('shortlist.key.credits.ai.paste.placeholder');
    const dropzoneText = useI18n('upload.resume.dropzone');
    const [dropzoneKey, setDropzoneKey] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const previewBlobUrlRef = useRef<string | null>(null);
    const [prefetchingPreview, setPrefetchingPreview] = useState(false);
    const dropzoneRef = React.useRef(null);

    useEffect(() => {
        const storageFileId = uploadedFile?.storageFileId;

        const revokeCached = () => {
            if (previewBlobUrlRef.current) {
                URL.revokeObjectURL(previewBlobUrlRef.current);
                previewBlobUrlRef.current = null;
            }
        };

        if (!storageFileId) {
            revokeCached();
            setPrefetchingPreview(false);
            return;
        }

        let cancelled = false;
        setPrefetchingPreview(true);

        fetchAttachmentUrl(storageFileId, activeOrganizationAccount)
            .then((url) => {
                if (cancelled) {
                    if (url) URL.revokeObjectURL(url);
                    return;
                }
                revokeCached();
                previewBlobUrlRef.current = url || null;
            })
            .finally(() => {
                if (!cancelled) setPrefetchingPreview(false);
            });

        return () => {
            cancelled = true;
            revokeCached();
        };
    }, [uploadedFile?.storageFileId, activeOrganizationAccount]);

    const handleDropzoneChange = useCallback(
        (res: { data?: { reference?: { id?: string } } }, saveFile: { name: string; size: number }) => {
            const storageFileId = res?.data?.reference?.id;
            if (!storageFileId) return;
            onUploadedFileChange({
                name: saveFile.name,
                size: saveFile.size,
                storageFileId
            });
            onTextChange('');
        },
        [onTextChange, onUploadedFileChange]
    );

    const handleTextChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onTextChange(event.target.value);
            if (event.target.value.trim()) {
                onUploadedFileChange(null);
            }
        },
        [onTextChange, onUploadedFileChange]
    );

    const handleRemoveFile = useCallback(() => {
        onUploadedFileChange(null);
        setDropzoneKey((key) => key + 1);
    }, [onUploadedFileChange]);

    const handlePreviewFile = useCallback(() => {
        if (!uploadedFile?.storageFileId) return;

        const cached = previewBlobUrlRef.current;
        if (cached) {
            window.open(cached, '_blank', 'noopener,noreferrer');
            return;
        }

        if (prefetchingPreview) return;

        void (async () => {
            setPreviewLoading(true);
            try {
                const url = await fetchAttachmentUrl(uploadedFile.storageFileId, activeOrganizationAccount);
                if (url) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                }
            } finally {
                setPreviewLoading(false);
            }
        })();
    }, [activeOrganizationAccount, prefetchingPreview, uploadedFile?.storageFileId]);

    const fileMetaLabel = uploadedFile
        ? [convertBytesToMbsOrKbs(uploadedFile.size), getFileTypeLabel(uploadedFile.name)].filter(Boolean).join(' ')
        : '';

    return (
        <V gap={1.5} sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1 }}>
            {uploadedFile ? (
                <FormModalInfoAlert
                    descriptionToken="shortlist.key.credits.ai.info"
                    sx={{ pt: 0, pl: 0, pr: 0 }}
                    alertSx={{ alignItems: 'center' }}
                />
            ) : null}

            <Typography variant="overline" color="primary" fontWeight={500}>
                <I18n token="shortlist.key.credits.ai.source.heading" />
            </Typography>

            {uploadedFile ? (
                <H
                    sx={{
                        px: 1.5,
                        py: 1.25,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        justifyContent: 'space-between',
                        gap: 1.5
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: 'action.hover',
                            flexShrink: 0
                        }}
                    >
                        <Icons.Attachments fontSize="small" color="action" />
                    </Box>
                    <V gap={0.25} sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                            {uploadedFile.name}
                        </Typography>
                        {fileMetaLabel ? (
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {fileMetaLabel}
                            </Typography>
                        ) : null}
                    </V>
                    <H sx={{ gap: 0.5, justifyContent: 'flex-end' }}>
                        <IconButton
                            size="small"
                            onClick={handlePreviewFile}
                            disabled={previewLoading || prefetchingPreview}
                            aria-label="preview file"
                        >
                            <Icons.Visibility fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={handleRemoveFile} aria-label="remove file">
                            <Icons.Delete fontSize="small" />
                        </IconButton>
                    </H>
                </H>
            ) : isUploading ? (
                <Loading />
            ) : (
                <>
                    <TextField
                        multiline
                        minRows={6}
                        fullWidth
                        placeholder={pastePlaceholder}
                        value={text}
                        onChange={handleTextChange}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            alignSelf: 'stretch',
                            gap: 1.5
                        }}
                    >
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            <I18n token="dialog.or" />
                        </Typography>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                    </Box>
                    <Dropzone
                        key={dropzoneKey}
                        dropzoneRef={dropzoneRef}
                        filesLimit={1}
                        maxFileSize={KEY_CREDITS_AI_MAX_UPLOAD_BYTES}
                        acceptedFiles={KEY_CREDITS_AI_ACCEPTED_MIME.split(',')}
                        dropzoneText={dropzoneText}
                        showPreviews={false}
                        showPreviewsInDropzone={false}
                        disableDropzone={Boolean(text.trim())}
                        setIsUploading={setIsUploading}
                        onChange={handleDropzoneChange}
                    />
                </>
            )}
        </V>
    );
}
